import { useState, useRef } from 'react';
import { Flame, Leaf, Star, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { menuItems, categories } from '@/data/menu';
import type { Category, Allergen } from '@/types';

const TOAST_URL = 'https://order.toasttab.com/online/kpc-urban-korean-food-new-6083-w-irlo-bronson-memorial-hwy';

// Allergen labels mapping
const allergenLabels: Record<Allergen, string> = {
  gluten: 'Gluten',
  soy: 'Soy',
  dairy: 'Dairy',
  eggs: 'Eggs',
  peanuts: 'Peanuts',
  treenuts: 'Tree Nuts',
  shellfish: 'Shellfish',
  fish: 'Fish',
  sesame: 'Sesame',
};

// Allergen colors
const allergenColors: Record<Allergen, string> = {
  gluten: '#FFD700',
  soy: '#FFA500',
  dairy: '#87CEEB',
  eggs: '#F0E68C',
  peanuts: '#D2691E',
  treenuts: '#8B4513',
  shellfish: '#FF6347',
  fish: '#4682B4',
  sesame: '#DAA520',
};

export default function MenuSection() {
  const [activeCategory, setActiveCategory] = useState<Category>('appetizers');
  const categoryScrollRef = useRef<HTMLDivElement>(null);

  const filteredItems = menuItems.filter((item) => item.category === activeCategory);

  const scrollCategories = (direction: 'left' | 'right') => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200;
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section id="menu" className="py-20 lg:py-32 relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255, 215, 0, 0.3) 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-[#FFD700]/10 text-[#FFD700] border-[#FFD700]/30">
            Our Menu
          </Badge>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 font-['Orbitron']">
            <span className="gradient-text">Seoul Street</span>{' '}
            <span className="text-white">Flavors</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
            Authentic Korean dishes with a modern urban twist. Each item is crafted 
            with traditional recipes and the freshest ingredients.
          </p>

          {/* Toast Order Button */}
          <Button
            onClick={() => window.open(TOAST_URL, '_blank')}
            className="btn-neon text-white px-8 py-6 text-lg mb-4"
          >
            <ExternalLink className="w-5 h-5 mr-2" />
            Order Online Now
          </Button>
          <p className="text-xs text-muted-foreground mb-4">
            You will be redirected to our secure ordering platform
          </p>
          
          {/* Allergen Legend */}
          <div className="flex flex-wrap justify-center gap-2 text-[10px] text-muted-foreground">
            <span className="mr-2">Allergens:</span>
            {Object.entries(allergenLabels).map(([key, label]) => (
              <span
                key={key}
                className="px-1.5 py-0.5 rounded border"
                style={{ 
                  borderColor: allergenColors[key as Allergen],
                  backgroundColor: `${allergenColors[key as Allergen]}15`,
                }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Category Navigation */}
        <div className="relative mb-12">
          {/* Scroll Buttons */}
          <button
            onClick={() => scrollCategories('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur border border-[#FFD700]/30 flex items-center justify-center hover:bg-[#FFD700]/10 transition-colors lg:hidden"
          >
            <ChevronLeft className="w-5 h-5 text-[#FFD700]" />
          </button>
          <button
            onClick={() => scrollCategories('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur border border-[#FFD700]/30 flex items-center justify-center hover:bg-[#FFD700]/10 transition-colors lg:hidden"
          >
            <ChevronRight className="w-5 h-5 text-[#FFD700]" />
          </button>

          {/* Categories */}
          <div
            ref={categoryScrollRef}
            className="flex gap-2 overflow-x-auto scrollbar-hide px-12 lg:px-0 lg:justify-center"
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl whitespace-nowrap transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-[#FFD700] text-black shadow-lg shadow-[#FFD700]/30'
                    : 'bg-card text-foreground hover:bg-[#FFD700]/10 border border-border'
                }`}
              >
                <span className="text-sm font-medium">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="group glass-card rounded-2xl overflow-hidden hover:neon-border transition-all duration-300 hover:-translate-y-1"
            >
              {/* Image */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  {item.popular && (
                    <Badge className="bg-[#FFD700] text-black font-semibold">
                      <Star className="w-3 h-3 mr-1" />
                      Popular
                    </Badge>
                  )}
                  {item.spicy && (
                    <Badge className="bg-red-500 text-white">
                      <Flame className="w-3 h-3 mr-1" />
                      Spicy
                    </Badge>
                  )}
                  {item.vegetarian && (
                    <Badge className="bg-green-500 text-white">
                      <Leaf className="w-3 h-3 mr-1" />
                      Veggie
                    </Badge>
                  )}
                </div>
                {/* Order on Toast Button Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button
                    onClick={() => window.open(TOAST_URL, '_blank')}
                    className="btn-neon text-white rounded-full px-6"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Order on Toast
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-foreground group-hover:text-[#FFD700] transition-colors">
                    {item.name}
                  </h3>
                  <span className="font-bold text-[#FFD700]">${item.price.toFixed(2)}</span>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                  {item.description}
                </p>
                
                {/* Allergens */}
                {item.allergens && item.allergens.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="text-[10px] text-muted-foreground mr-1">Contains:</span>
                    {item.allergens.map((allergen) => (
                      <span
                        key={allergen}
                        className="text-[10px] px-1.5 py-0.5 rounded border text-white/80"
                        style={{ 
                          borderColor: allergenColors[allergen],
                          backgroundColor: `${allergenColors[allergen]}20`,
                        }}
                        title={allergenLabels[allergen]}
                      >
                        {allergenLabels[allergen]}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <Button
            onClick={() => window.open(TOAST_URL, '_blank')}
            className="btn-neon text-white px-10 py-6 text-lg"
          >
            <ExternalLink className="w-5 h-5 mr-2" />
            Order Online — Pickup at KPC
          </Button>
          <p className="text-sm text-muted-foreground mt-3">
            Secure checkout powered by Toast
          </p>
        </div>
      </div>
    </section>
  );
}
